import { useState } from 'react';
import { ChevronDown, ChevronRight, Info, Pencil } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import type { PruefAnleitungDto } from '../../lib/api/abnahme';

const ORTE: PruefAnleitungDto['wo'][] = [
  'Website',
  'Kundenbereich',
  'Office',
  'Lehrerportal',
  'E-Mail-Postfach',
  'Technik',
];

type Entwurf = {
  wo: PruefAnleitungDto['wo'];
  vorbereitung: string;
  schritte: string;
  erwartung: string;
  hinweis: string;
};

const alsEntwurf = (anleitung?: PruefAnleitungDto | null): Entwurf => ({
  wo: anleitung?.wo ?? 'Office',
  vorbereitung: anleitung?.vorbereitung ?? '',
  schritte: (anleitung?.schritte ?? []).join('\n'),
  erwartung: anleitung?.erwartung ?? '',
  hinweis: anleitung?.hinweis ?? '',
});

/**
 * „So prüfst du das“ — die Anleitung zu einem Akzeptanzkriterium, aufklappbar
 * (docs/abnahme-plan.md, Abschnitt 13). Für Stakeholder ohne Entwicklerblick:
 * wo, was vorbereiten, welche Schritte, was soll passieren.
 *
 * Mit `onSpeichern` lässt sie sich bearbeiten oder neu anlegen; was hier
 * gespeichert wird, überschreibt das Einspielen einer neuen Fassung nicht mehr.
 */
export default function PruefAnleitung({
  anleitung,
  onSpeichern,
}: {
  anleitung?: PruefAnleitungDto | null;
  onSpeichern?: (anleitung: Omit<PruefAnleitungDto, 'quelle'> | null) => Promise<boolean> | boolean;
}) {
  const [offen, setOffen] = useState(false);
  const [entwurf, setEntwurf] = useState<Entwurf | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const speichern = async () => {
    if (!entwurf || !onSpeichern) return;
    setSpeichert(true);
    const leer = !entwurf.schritte.trim() && !entwurf.erwartung.trim() && !entwurf.hinweis.trim();
    const ok = await onSpeichern(
      leer
        ? null
        : {
            wo: entwurf.wo,
            vorbereitung: entwurf.vorbereitung.trim() || null,
            schritte: entwurf.schritte
              .split('\n')
              .map((zeile) => zeile.replace(/^\s*\d+[.)]\s*/, '').trim())
              .filter(Boolean),
            erwartung: entwurf.erwartung.trim(),
            hinweis: entwurf.hinweis.trim() || null,
          },
    );
    setSpeichert(false);
    if (ok) {
      setEntwurf(null);
      setOffen(true);
    }
  };

  if (entwurf) {
    return (
      <div className="mt-1.5 space-y-2 rounded-md border p-3 text-sm">
        <div className="grid gap-2 sm:grid-cols-[10rem_1fr]">
          <div className="space-y-1">
            <Label htmlFor="anleitung-wo">Wo</Label>
            <select
              id="anleitung-wo"
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={entwurf.wo}
              onChange={(e) => setEntwurf({ ...entwurf, wo: e.target.value as Entwurf['wo'] })}
            >
              {ORTE.map((ort) => (
                <option key={ort} value={ort}>
                  {ort}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="anleitung-vorbereitung">Vorbereitung</Label>
            <Input
              id="anleitung-vorbereitung"
              value={entwurf.vorbereitung}
              onChange={(e) => setEntwurf({ ...entwurf, vorbereitung: e.target.value })}
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor="anleitung-schritte">Schritte (einer je Zeile)</Label>
          <Textarea
            id="anleitung-schritte"
            rows={4}
            value={entwurf.schritte}
            onChange={(e) => setEntwurf({ ...entwurf, schritte: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="anleitung-erwartung">Erwartet</Label>
          <Textarea
            id="anleitung-erwartung"
            rows={2}
            value={entwurf.erwartung}
            onChange={(e) => setEntwurf({ ...entwurf, erwartung: e.target.value })}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="anleitung-hinweis">Hinweis</Label>
          <Input
            id="anleitung-hinweis"
            value={entwurf.hinweis}
            onChange={(e) => setEntwurf({ ...entwurf, hinweis: e.target.value })}
          />
        </div>
        <div className="flex gap-2">
          <Button size="sm" disabled={speichert} onClick={() => void speichern()}>
            Anleitung speichern
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEntwurf(null)}>
            Abbrechen
          </Button>
        </div>
      </div>
    );
  }

  if (!anleitung) {
    return onSpeichern ? (
      <button
        type="button"
        onClick={() => setEntwurf(alsEntwurf(null))}
        className="text-xs text-muted-foreground hover:underline"
      >
        + Prüfanleitung anlegen
      </button>
    ) : null;
  }

  const technik = anleitung.wo === 'Technik';
  return (
    <div className="text-xs">
      <span className="inline-flex items-center gap-2">
        <button
          type="button"
          onClick={() => setOffen((v) => !v)}
          className="inline-flex items-center gap-1 text-primary hover:underline"
          aria-expanded={offen}
        >
          {offen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
          {technik ? 'Warum nicht von Hand prüfbar?' : `So prüfst du das (${anleitung.wo})`}
        </button>
        {onSpeichern && (
          <button
            type="button"
            onClick={() => setEntwurf(alsEntwurf(anleitung))}
            className="text-muted-foreground hover:text-foreground"
            aria-label="Prüfanleitung bearbeiten"
          >
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </span>
      {offen && (
        <div className="mt-1.5 space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
          {anleitung.vorbereitung && (
            <p>
              <span className="font-medium">Vorbereitung: </span>
              {anleitung.vorbereitung}
            </p>
          )}
          {anleitung.schritte.length > 0 && (
            <ol className="list-decimal space-y-1 pl-5">
              {anleitung.schritte.map((schritt, index) => (
                <li key={index}>{schritt}</li>
              ))}
            </ol>
          )}
          {anleitung.erwartung && (
            <p>
              <span className="font-medium">Erwartet: </span>
              {anleitung.erwartung}
            </p>
          )}
          {anleitung.hinweis && (
            <p className="flex gap-1.5 text-muted-foreground">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{anleitung.hinweis}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
